import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Leaf, Sparkles } from 'lucide-react';
import Reveal from '../components/Reveal';
import { images, brands, sinthiaBrand } from '../mock';
import sinthiaPhoto from '../assets/sinthia-photo.jpg';
import machinePhoto from '../assets/machine.png';

const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative h-[46vh] min-h-[340px] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center slide-zoom" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/90 via-[#0f2c1f]/70 to-[#0f2c1f]/90" />
    <div className="relative z-10 text-center px-5 pt-16 max-w-3xl">
      <p className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-4">{eyebrow}</p>
      <h1 className="animate-fade-up font-serif-display text-white text-4xl md:text-6xl font-bold text-shadow-lg" style={{ animationDelay: '0.15s' }}>{title}</h1>
      {subtitle && <p className="animate-fade-up text-white/80 mt-4" style={{ animationDelay: '0.25s' }}>{subtitle}</p>}
    </div>
  </section>
);

const highlights = {
  sinthia: ['Single-origin ground spices', 'Authentic traditional formulas', 'Sealed for organic freshness'],
  'kst-spices': ['Aromatic wholesale selections', 'Whole herbs & seed spices', 'Blended traditional seasonings'],
  grains: ['Karuppu Kavuni (black rice)', 'Mappillai Samba', 'Seeraga Samba', 'Thooyamalli', 'Premium aged Basmati'],
  produce: ['Farm-to-table freshness', 'Daily cold-chain arrivals', 'Hand-selected quality'],
};

const Brands = () => {
  return (
    <div>
      <PageHero
        eyebrow="EXCLUSIVE HOUSE SIGNATURES"
        title="Sinthia & KST Brands"
        subtitle="We source directly from subcontinental agricultural horizons to bring you signature organic collections under our globally trusted house labels."
        image={images.spiceDisplay}
      />

      {/* Sinthia brand story */}
      <section className="py-20 md:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="flex flex-col gap-5">
              <div className="relative rounded-[24px] overflow-hidden shadow-xl">
                <img
                  src={sinthiaPhoto}
                  alt="Sinthia curated spices and grains"
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="relative rounded-[24px] overflow-hidden shadow-xl bg-[#faf7f0] border border-[#e8e0cf] flex items-center justify-center p-6">
                <img
                  src={machinePhoto}
                  alt="Sinthia grocery packing machine"
                  className="h-[320px] w-auto object-contain"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#163a29] text-[#dcbf74] text-xs font-semibold tracking-[0.2em] mb-5">
              <Sparkles size={14} /> OWN BRAND
            </span>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold leading-tight mb-2">Sinthia</h2>
            <p className="font-serif-display italic text-[#c8a24c] text-xl md:text-2xl mb-6">{sinthiaBrand.tagline}</p>
            <p className="text-[#3a4a42] leading-relaxed mb-5">{sinthiaBrand.paragraph}</p>
            <p className="text-[#3a4a42] leading-relaxed mb-8">{sinthiaBrand.paragraph2}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {sinthiaBrand.highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 text-[#163a29] text-sm">
                  <span className="w-6 h-6 rounded-full bg-[#c8a24c]/20 flex items-center justify-center shrink-0">
                    <Leaf size={13} className="text-[#163a29]" />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Other house brands */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-[#c8a24c] text-sm font-semibold tracking-[0.3em] mb-3 uppercase">More House Signatures</p>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold">Our Flagship Collections</h2>
          </Reveal>
          <div className="space-y-16 md:space-y-24">
            {brands.filter((b) => b.id !== 'sinthia').map((b, i) => (
              <Reveal key={b.id}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                  <div className="img-zoom-parent relative rounded-3xl overflow-hidden shadow-xl [direction:ltr]">
                    <img src={b.image} alt={b.name} className="img-zoom w-full h-[380px] object-cover" />
                    <span className="absolute top-4 left-4 px-4 py-1.5 rounded-full bg-[#0f2c1f]/90 text-[#c8a24c] text-xs font-semibold tracking-widest">{b.tag}</span>
                  </div>
                  <div className="[direction:ltr]">
                    <h2 className="font-serif-display text-[#163a29] text-3xl md:text-4xl font-bold mb-5">{b.name}</h2>
                    <p className="text-[#3a4a42] leading-relaxed mb-6">{b.description}</p>
                    <ul className="space-y-3 mb-8">
                      {highlights[b.id].map((h) => (
                        <li key={h} className="flex items-center gap-3 text-[#163a29]">
                          <span className="w-6 h-6 rounded-full bg-[#c8a24c]/20 flex items-center justify-center shrink-0">
                            <Check size={14} className="text-[#163a29]" />
                          </span>
                          {h}
                        </li>
                      ))}
                    </ul>
                    <Link to="/stores" className="group inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#163a29] text-white font-semibold hover:bg-[#0f2c1f] transition-all duration-300">
                      Find in Store
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Brands;
